import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useTabVisibility from '~/hooks/browser/useTabVisibility';
import useIsOnline from '~/hooks/network/useIsOnline';
import { getAccessToken } from '~/services/localStorage/auth';
import { useStoreActions, useStoreState } from '~/store/hooks';
import isDev from '~/utils/isDev';

interface ISignalRTokenContext {
  token: string | null;
  isOnline: boolean;
  handleError: (error?: Error) => Promise<void>;
  handleConnection: () => void;
}

const SignalRTokenContext = createContext<ISignalRTokenContext | null>(null);

interface IProps {
  children: ReactNode | ReactNode[];
}

const MAX_RETRIES = 5;
const BACKOFF_BASE_MS = 2000;
const MAX_BACKOFF_MS = 60000;

export const SignalRTokenProvider = ({ children }: IProps): ReactElement => {
  const [token, setToken] = useState<string | null>(null);
  const [inErrorBackoff, setInErrorBackoff] = useState(false);
  const triesRef = useRef(0);
  const backoffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { current } = useStoreState(state => state.user);
  const isOnline = useIsOnline();
  const isTabActive = useTabVisibility();

  const { onGetCurrentProfile } = useStoreActions(state => state.user);

  const connectionCloseDelay = () =>
    new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });

  const scheduleRetry = useCallback(() => {
    if (backoffTimeoutRef.current) {
      clearTimeout(backoffTimeoutRef.current);
    }
    const tries = triesRef.current;
    const delay =
      tries >= MAX_RETRIES
        ? MAX_BACKOFF_MS
        : Math.min(BACKOFF_BASE_MS * 2 ** tries, MAX_BACKOFF_MS);

    backoffTimeoutRef.current = setTimeout(() => {
      backoffTimeoutRef.current = null;
      setInErrorBackoff(false);
    }, delay);
  }, []);

  // eslint-disable-next-line consistent-return
  const handleError = async (error?: Error) => {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('error: ', JSON.stringify(error));
    }

    setInErrorBackoff(true);
    setToken(null);

    if (triesRef.current >= MAX_RETRIES) {
      scheduleRetry();
      return connectionCloseDelay();
    }
    triesRef.current += 1;

    // Trigger Axios interceptor to refresh the token upon encountering an error and tab is active.
    // The error can stem from various issues, such as a loss of internet connectivity, server unreachability, or a SignalR service outage.
    // A maximum of 5 retry attempts will be made. The retry counter will be reset upon a page refresh or restoration of network connectivity.
    // If the token refresh process fails, the user will be logged out by the Axios interceptor.
    if (!isTabActive) {
      return undefined;
    }
    await onGetCurrentProfile();
    scheduleRetry();
  };

  const handleConnection = useCallback((): void => {
    triesRef.current = 0;
    setInErrorBackoff(false);
    if (backoffTimeoutRef.current) {
      clearTimeout(backoffTimeoutRef.current);
      backoffTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const accessToken = getAccessToken();

    if (!inErrorBackoff && accessToken && !token && current) {
      setToken(accessToken);
    } else if (!accessToken) {
      setToken(null);
    }
  }, [token, current, inErrorBackoff]);

  useEffect(() => {
    if (isOnline) {
      triesRef.current = 0;
      setInErrorBackoff(false);
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
        backoffTimeoutRef.current = null;
      }
      setToken(null);
    }
  }, [isOnline]);

  useEffect(
    () => () => {
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
      }
    },
    [],
  );

  const contextValue: ISignalRTokenContext = {
    token,
    isOnline,
    handleError,
    handleConnection,
  };

  return (
    <SignalRTokenContext.Provider value={contextValue}>{children}</SignalRTokenContext.Provider>
  );
};

export const useSignalRToken = (): ISignalRTokenContext => {
  const context = useContext(SignalRTokenContext);
  if (!context) {
    throw new Error('useSignalRToken must be used within a SignalRTokenProvider');
  }
  return context;
};
